<template>
  <div v-if="firmwareForm" class="py-8">
    <Card>
      <template #content>
        <div class="flex flex-col gap-8">

          <FormGroup :label="$t('forms.firmware.firmware_properties_form.version_number')"
                     :error-text="firmwareForm.errors?.version_number">
            <InputText
                v-model="firmwareForm.version_number"
                placeholder="e.g. 1.0.0"
                @change="firmwareForm.validate('version_number')"
                data-cy="firmware-version-number-input"
            />
          </FormGroup>

          <FormGroup :label="$t('forms.firmware.firmware_properties_form.firmware_type')"
                     :error-text="firmwareForm.errors?.firmware_type_id">
            <Dropdown
                v-model="firmwareForm.firmware_type_id"
                :options="firmwareTypesStore.getFirmwareTypeOptions"
                option-label="name"
                option-value="id"
                @change="firmwareForm.validate('firmware_type_id')"
                data-cy="firmware-type-input"
            />
          </FormGroup>

          <FormGroup :label="$t('forms.firmware.firmware_properties_form.device_type')"
                     :error-text="firmwareForm.errors?.device_type_id">
            <Dropdown
                v-model="firmwareForm.device_type_id"
                :options="deviceTypesStore.getDeviceTypeOptions"
                option-label="name"
                option-value="id"
                @change="onDeviceTypeChange"
                data-cy="device-type-input"
            />
          </FormGroup>

          <FormGroup :label="$t('forms.firmware.firmware_properties_form.device_model')"
                     :error-text="firmwareForm.errors?.device_model_id">
            <Dropdown
                v-model="firmwareForm.device_model_id"
                :options="deviceModels"
                option-label="name"
                option-value="id"
                @change="onDeviceModelChange"
                :disabled="!firmwareForm.device_type_id"
                data-cy="device-model-input"
            />
          </FormGroup>

          <FormGroup :label="$t('forms.firmware.firmware_properties_form.applicable_device_revisions')"
                     :error-text="firmwareForm.errors?.device_revision_ids">
            <MultiSelect
                v-model="firmwareForm.device_revision_ids"
                :options="deviceRevisions"
                option-label="name"
                option-value="id"
                :placeholder="$t('forms.firmware.firmware_properties_form.select_device_revisions')"
                display="chip"
                class="w-full"
                :disabled="!firmwareForm.device_model_id"
                @change="onDeviceRevisionChange"
                data-cy="applicable-device-revisions"
            >
              <template #option="slotProps">
                <div>{{ slotProps.option.name }} - <span class="font-light">{{ slotProps.option.description }}</span></div>
              </template>
              <template #footer>
                <div class="py-2 px-3">
                  {{ $t('forms.common.x_items_selected', firmwareForm.device_revision_ids?.length ?? 0, { count: firmwareForm.device_revision_ids?.length ?? 0 }) }}
                </div>
              </template>
            </MultiSelect>
          </FormGroup>

          <FormGroup :label="$t('forms.firmware.firmware_properties_form.relevant_regulations')"
                     :error-text="firmwareForm.errors?.regulation_ids">
            <MultiSelect
                v-model="firmwareForm.regulation_ids"
                :options="availableRegulations"
                option-label="name"
                option-value="id"
                :placeholder="$t('forms.firmware.firmware_properties_form.select_regulations')"
                display="chip"
                class="w-full"
                @change="firmwareForm.validate('regulation_ids')"
                data-cy="relevant-regulations"
            >
              <template #option="slotProps">
                <div>{{ slotProps.option.name }} - <span class="font-light">{{ slotProps.option.description }}</span></div>
              </template>
              <template #footer>
                <div class="py-2 px-3">
                  {{ $t('forms.common.x_items_selected', firmwareForm.regulation_ids?.length ?? 0, { count: firmwareForm.regulation_ids?.length ?? 0 }) }}
                </div>
              </template>
            </MultiSelect>
          </FormGroup>

          <FileInput
              v-model="firmwareForm.firmware_file"
              :label="$t('forms.firmware.firmware_properties_form.firmware_file')"
              :help-text="$t('pages.admin.devices.device_third_party_firmwares.properties.device_third_party_firmware_view.file_optional')"
              :error-text="firmwareForm.errors?.firmware_file"
              @change="firmwareForm.validate('firmware_file')"
          />

          <FormGroup
              :label="$t('forms.firmware.firmware_properties_form.notes')"
              :error-text="firmwareForm.errors?.notes"
          >
            <Textarea
                v-model="firmwareForm.notes"
                rows="4"
                @change="firmwareForm.validate('notes')"
                :placeholder="$t('forms.firmware.firmware_properties_form.notes_placeholder')"
            />
          </FormGroup>

        </div>
      </template>

      <template #footer>
        <div class="flex justify-end">
          <Button @click="saveFirmware" :label="$t('forms.common.save')" :disabled="firmwareForm.processing" data-cy="third-party-firmware-savebtn" />
        </div>
      </template>
    </Card>
  </div>
</template>

<script>
import {mapStores} from "pinia";
import {useFirmwaresStore} from "@/stores/firmwares.js";
import {useFirmwareTypesStore} from "@/stores/firmwareTypes.js";
import {useDeviceTypesStore} from "@/stores/deviceTypes.js";
import {useDeviceModelsStore} from "@/stores/deviceModels.js";
import {useDeviceRevisionsStore} from "@/stores/deviceRevisions.js";
import {useRegulationsStore} from "@/stores/regulations.js";
import {useAuthStore} from "@/stores/auth.js";
import FormGroup from "@/Components/FormGroup.vue";
import FileInput from "@/Components/FileInput.vue";

export default {
  name: "DeviceThirdPartyFirmwarePropertiesView",
  components: {FormGroup, FileInput},
  data() {
    return {
      firmwareForm: undefined,
    };
  },
  computed: {
    ...mapStores(useFirmwaresStore),
    ...mapStores(useFirmwareTypesStore),
    ...mapStores(useDeviceTypesStore),
    ...mapStores(useDeviceModelsStore),
    ...mapStores(useDeviceRevisionsStore),
    ...mapStores(useRegulationsStore),
    ...mapStores(useAuthStore),
    firmwareCompanyId() {
      return this.firmwareForm?.company_id ?? this.authStore.user?.primary_company_id ?? null;
    },
    deviceModels() {
      const companyId = this.firmwareCompanyId;
      return this.deviceModelsStore.getDeviceModelOptions?.filter(
          m => this.sameId(m.device_type_id, this.firmwareForm?.device_type_id)
              && this.isNonPublicDeviceModel(m)
              && (!companyId || this.deviceModelAvailableForCompany(m, companyId))
      ) || [];
    },
    deviceRevisions() {
      return this.deviceRevisionsStore.getDeviceRevisionOptions?.filter(
          r => this.sameId(r.device_model_id, this.firmwareForm?.device_model_id)
      ) || [];
    },
    availableRegulations() {
      if (!this.firmwareForm?.device_revision_ids?.length) {
        return [];
      }
      const regulations = this.deviceRevisions
          ?.filter(r => this.firmwareForm.device_revision_ids.some(id => this.sameId(id, r.id)))
          ?.flatMap(r => r.regulations ?? []) || [];
      return [...new Map(regulations.map(regulation => [String(regulation.id), regulation])).values()];
    },
    availableRegulationIds() {
      return this.availableRegulations.map(regulation => String(regulation.id));
    },
    availableRegulationKey() {
      return this.availableRegulationIds.sort().join('|');
    },
  },
  methods: {
    onDeviceTypeChange() {
      this.firmwareForm.device_model_id = null;
      this.onDeviceModelChange();
    },
    onDeviceModelChange() {
      this.firmwareForm.device_revision_ids = [];
      this.pruneUnavailableRegulations();
      this.firmwareForm.validate();
    },
    onDeviceRevisionChange() {
      this.pruneUnavailableRegulations();
      this.firmwareForm.validate('device_revision_ids');
    },
    pruneUnavailableRegulations() {
      if (!this.firmwareForm) {
        return;
      }

      const availableIds = new Set(this.availableRegulationIds);
      this.firmwareForm.regulation_ids = (this.firmwareForm.regulation_ids ?? [])
          .filter(regulationId => availableIds.has(String(regulationId)));
    },
    isNonPublicDeviceModel(deviceModel) {
      return !!deviceModel.company_id || !!deviceModel.company_ids?.length;
    },
    deviceModelAvailableForCompany(deviceModel, companyId) {
      if (deviceModel.company_ids?.length) {
        return deviceModel.company_ids.some(id => this.sameId(id, companyId));
      }

      return this.sameId(deviceModel.company_id, companyId);
    },
    sameId(left, right) {
      return left != null && right != null && String(left) === String(right);
    },
    async saveFirmware() {
      try {
        this.pruneUnavailableRegulations();
        const response = await this.firmwareForm.submit();
        this.$toast.add({
          severity: 'success',
          summary: this.$t('common.complete'),
          detail: this.$t('pages.admin.devices.device_third_party_firmwares.properties.device_third_party_firmware_view.firmware_saved'),
          life: 3000,
        });
        this.$emit('on-save', response.data.version_number);
        this.$router.replace({ params: { ...this.$route.params, firmware: response.data.id } });
      } catch (error) {
        //
      }
    },
    async loadOptions() {
      await Promise.all([
        this.firmwareTypesStore.indexFirmwareTypeOptions(),
        this.deviceTypesStore.indexDeviceTypeOptions(),
        this.deviceModelsStore.indexDeviceModelOptions({}, false),
        this.deviceRevisionsStore.indexDeviceRevisionOptions({ include: ['regulations'] }),
        this.regulationsStore.indexRegulationOptions(),
      ]);
    },
  },
  emits: ['on-save', 'on-delete', 'on-loaded'],
  watch: {
    '$route.params.firmware': {
      async handler(firmwareId) {
        if (!firmwareId) {
          this.firmwareForm = undefined;
          return;
        }
        await this.loadOptions();
        if (firmwareId === 'NEW') {
          this.firmwareForm = await this.firmwaresStore.createFirmware();
          this.firmwareForm.purpose = 'third_party';
        } else {
          this.firmwareForm = await this.firmwaresStore.editFirmware(firmwareId);
          this.firmwareForm.purpose = 'third_party';
          this.pruneUnavailableRegulations();
          this.$emit('on-loaded', this.firmwareForm.version_number);
        }
      },
      immediate: true,
    },
    availableRegulationKey() {
      this.pruneUnavailableRegulations();
    },
  },
};
</script>
